import React from 'react'

import Analytics from 'app/pages/Analytics/Analytics'

import AtHomeAttended from './Attended'
import AtHomeBookings from './Bookings'
import AtHomeChanges from './Changes'
import AtHomeFeedback from './Feedback'
import AtHomeIssues from './Issues'
import AtHomeListings from './Listings'
import AtHomePlans from './Plans'
import {
  AtHomeApartmentsSetup,
  AtHomeCategoriesSetup,
  AtHomeModulesSetup,
  AtHomePricingSetup
} from './Setup'
import TabbedPage from './shared/TabbedPage'
import AtHomeSlots from './Slots'
import AtHomeSupportContact from './SupportContact'

/**
 * At Home (1:1 classes at the child's home) — the admin workspace. Not related
 * to At Apartment. Five left-panel pages (owner, 2026-10-03), each with its
 * views as tabs on the right. Backend: /api/v1/oh_at_home/admin/*.
 */
const kinds = (Comp) => [
  { id: 'trial', label: 'Trial', render: () => <Comp kind="trial" /> },
  { id: 'regular', label: 'Regular', render: () => <Comp kind="regular" /> }
]

// Schedules listed: the scheduling team lists and edits. Trial = dated slots
// (Slots.js); Regular = weekly listings modules are booked on (Listings.js).
// No booking here (management, 2026-10-09: scheduling and booking are
// different people).
export const AtHomeSchedulesPage = () => (
  <TabbedPage
    storageKey="atHome.schedules.tab"
    tabs={[
      {
        id: 'trial',
        label: 'Trial',
        render: () => <AtHomeSlots kind="trial" mode="schedule" />
      },
      {
        id: 'regular',
        label: 'Regular',
        render: () => <AtHomeListings mode="schedule" />
      }
    ]}
  />
)

// Book a session: the booking team's work list — open trial slots to
// book, and listings a module can be booked on.
export const AtHomeToBookPage = () => (
  <TabbedPage
    storageKey="atHome.tobook.tab"
    tabs={[
      {
        id: 'trial',
        label: 'Trial',
        render: () => <AtHomeSlots kind="trial" mode="book" />
      },
      {
        id: 'regular',
        label: 'Regular',
        render: () => <AtHomeListings mode="book" />
      }
    ]}
  />
)

// Bookings: what has been booked — trials, and regular bookings with their sessions.
export const AtHomeBookingsPage = () => (
  <TabbedPage
    storageKey="atHome.bookings.tab"
    tabs={[
      {
        id: 'trial',
        label: 'Trial',
        render: () => <AtHomeBookings kind="trial" />
      },
      // One row per regular booking (module plan now, monthly plan later);
      // its sessions open from the row.
      { id: 'regular', label: 'Regular', render: () => <AtHomePlans /> },
      // Every cancellation and reschedule, one row per change (owner, 2026-10-19).
      {
        id: 'changes',
        label: 'Cancelled & rescheduled',
        render: () => <AtHomeChanges />
      }
    ]}
  />
)

// Feedback: parents' WhatsApp quick replies, trial and regular together.
export const AtHomeAttendedPage = () => (
  <TabbedPage
    storageKey="atHome.attended.tab"
    tabs={[
      ...kinds(AtHomeAttended),
      { id: 'feedback', label: 'Feedback', render: () => <AtHomeFeedback /> }
    ]}
  />
)

// Dashboard: the At Home analytics group (oh-api analytics/registry.yaml,
// group at_home), drawn by the shared Analytics page so its sub-tabs, date
// filter and click-to-list behave like every other Alfred dashboard.
export const AtHomeDashboardPage = () => <Analytics group="at_home" />

export const AtHomeIssuesPage = () => (
  <TabbedPage
    storageKey="atHome.issues.tab"
    tabs={[{ id: 'issues', label: 'Issues', render: () => <AtHomeIssues /> }]}
  />
)

export const AtHomeSetupPage = () => (
  <TabbedPage
    storageKey="atHome.setup.tab"
    tabs={[
      {
        id: 'categories',
        label: 'Categories',
        render: () => <AtHomeCategoriesSetup />
      },
      {
        id: 'apartments',
        label: 'Apartments',
        render: () => <AtHomeApartmentsSetup />
      },
      {
        id: 'modules',
        label: 'Projects',
        render: () => <AtHomeModulesSetup />
      },
      { id: 'pricing', label: 'Pricing', render: () => <AtHomePricingSetup /> },
      {
        id: 'support',
        label: 'Support',
        render: () => <AtHomeSupportContact />
      }
    ]}
  />
)

export default AtHomeSchedulesPage
